import React, { useEffect, useMemo, useState } from 'react';
import { ventesAdd as api } from '../services/api';
import { nb, eurCourt, pct1, moisCourt, useFiltresVentes, PanneauVentes, usePanneau, basculer } from './ventesUtils';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './VentesJour.css';  // rg-* du regroupement, vj-sel
import './Ventes.css';
import './Stock.css';       // st-tools, st-seg

// ─── APV — Ventes additionnelles (Auto Antilles) — 2026-10-06 ───────────────
// Ce que l'atelier vend en plus sur ses OR payants, par famille (référentiel
// en Administration ▸ Ventes additionnelles). L'indicateur principal est le
// TAUX DE VENTE : part des OR payants contenant la famille (pièce ou MO).
//
// Le serveur renvoie un cube (société × mois × atelier × marque × famille) :
// tout se recalcule ici, et chaque bloc ignore son propre filtre (CLAUDE.md
// §4 bis, règle 4) — le bloc par concession ignore les sociétés, celui par
// atelier l'atelier, celui par marque la marque. Les taux d'un regroupement se
// RECALCULENT depuis ses comptes, jamais en moyenne de taux.
//
// Potentiel : ce que rapporterait chaque concession retenue si elle vendait la
// famille aussi souvent que la meilleure (au moins 200 OR payants pour être
// une référence), au panier MOYEN de la famille (toutes concessions). Le
// panier propre d'une concession est instable sur de petits volumes : le 06/10,
// 35 recharges de clim à 339 € chez Blue, multipliées par l'écart à un aérosol
// à 8 € vendu sur 11 % des OR ailleurs, donnaient 268 k€ de « potentiel ».

const ATELIERS = ['Mécanique', 'Rapide'];
const MESURES = [{ c: 'taux', l: 'Taux de vente' }, { c: 'ca', l: 'CA pièces + MO' }, { c: 'n', l: "Nombre d'OR" }];
const SEUIL_REFERENCE = 200;
const taux = (n, ors) => (ors > 0 ? (n / ors) * 100 : null);
const pt = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} pt`);
const vide = () => ({ n: 0, p: 0, mg: 0, mo: 0 });

export default function ApvVentesAdd() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [mesure, setMesure] = useState('taux');
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  const atelier = f.params.get('atelier') || '';
  const marque = f.params.get('marque') || '';

  useEffect(() => {
    let actif = true; setErreur('');
    api.charger({ periode: f.periode, mois: f.moisStr })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [f.periode, f.moisStr]);

  // ── Le moteur : comptes d'un sous-ensemble du cube ──
  const N = useMemo(() => new Set(data?.mois || []), [data]);
  const N1 = useMemo(() => new Set(data?.moisN1 || []), [data]);
  const fams = useMemo(() => data?.familles || [], [data]);
  const selSet = useMemo(() => new Set(f.selected), [f.selected]);
  // `sauf` : le filtre que le bloc ignore ('soc' | 'at' | 'mq').
  const garde = (r, per, sauf) => (per === 'N1' ? N1 : N).has(r.ym)
    && (sauf === 'soc' || selSet.has(r.s))
    && (sauf === 'at' || !atelier || r.at === atelier)
    && (sauf === 'mq' || !marque || r.mq === marque);
  const comptes = (per, sauf, cle = () => '') => {
    const out = new Map();
    const de = (k) => { if (!out.has(k)) out.set(k, { ors: 0, f: {} }); return out.get(k); };
    (data?.base || []).forEach((r) => { if (garde(r, per, sauf)) de(cle(r)).ors += r.n; });
    (data?.ventes || []).forEach((r) => {
      if (!garde(r, per, sauf)) return;
      const x = de(cle(r)); const y = x.f[r.f] || (x.f[r.f] = vide());
      y.n += r.n; y.p += r.p; y.mg += r.mg; y.mo += r.mo;
    });
    return out;
  };
  const somme = (liste) => liste.reduce((a, x) => {
    a.ors += x.ors;
    Object.entries(x.f).forEach(([k, y]) => { const z = a.f[k] || (a.f[k] = vide()); z.n += y.n; z.p += y.p; z.mg += y.mg; z.mo += y.mo; });
    return a;
  }, { ors: 0, f: {} });

  // ── Bloc par concession (ignore le filtre sociétés) ──
  const parSoc = useMemo(() => comptes('N', 'soc', (r) => r.s),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, atelier, marque]);
  const maille = useRegroupement();
  const feuilles = useMemo(() => societesDispo.filter((s) => parSoc.has(s.code))
    .map((s) => ({ ...s, libelle: s.nom, ...parSoc.get(s.code) })), [societesDispo, parSoc]);
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  const arbre = useMemo(() => construireArbre(feuilles, maille.niveau, (m) => somme(m), maille.ouverts, (x) => x.ors),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);

  const Panneau = (
    <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo}
      extra={{ choix: { titre: 'Atelier', valeurs: [{ code: '', nom: 'Tous' }, ...ATELIERS.map((a) => ({ code: a, nom: a }))],
        valeur: atelier, onChoisir: (c) => f.setFiltre({ atelier: c }) } }} />
  );
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  // ── Totaux sous tous les filtres ──
  const tN = somme([...comptes('N').values()]);
  const tN1 = somme([...comptes('N1').values()]);
  const caDe = (x) => Object.values(x.f).reduce((a, y) => a + y.p + y.mo, 0);
  const mgDe = (x) => Object.values(x.f).reduce((a, y) => a + y.mg, 0);
  const pDe = (x) => Object.values(x.f).reduce((a, y) => a + y.p, 0);

  // ── Potentiel par famille (sociétés retenues, référence = meilleure société) ──
  const potentiel = {};
  fams.forEach((fa) => {
    const lignes = feuilles.map((s) => ({ code: s.code, ors: s.ors, y: s.f[fa.code] || vide() }));
    const refs = lignes.filter((l) => l.ors >= SEUIL_REFERENCE);
    const best = refs.length ? Math.max(...refs.map((l) => taux(l.y.n, l.ors) || 0)) : null;
    const tot = somme(lignes.map((l) => ({ ors: l.ors, f: { x: l.y } }))).f.x || vide();
    const panierMoyen = tot.n ? (tot.p + tot.mo) / tot.n : 0;
    potentiel[fa.code] = best == null ? 0 : lignes.filter((l) => selSet.has(l.code)).reduce((a, l) => {
      const t = taux(l.y.n, l.ors) || 0; if (t >= best) return a;
      return a + ((best - t) / 100) * l.ors * panierMoyen;
    }, 0);
  });
  const potentielTotal = Object.values(potentiel).reduce((a, v) => a + v, 0);

  // Comptoir : sociétés et période seulement (ni atelier ni marque au comptoir).
  const comptoir = {};
  (data.comptoir || []).forEach((r) => { if (N.has(r.ym) && selSet.has(r.s)) { const c = comptoir[r.f] || (comptoir[r.f] = { n: 0, p: 0, mg: 0 }); c.n += r.n; c.p += r.p; c.mg += r.mg; } });

  const cellule = (x, code) => {
    const y = x.f[code] || vide();
    if (mesure === 'taux') return pct1(taux(y.n, x.ors));
    if (mesure === 'ca') return y.p + y.mo ? eurCourt(y.p + y.mo) : '·';
    return y.n ? nb(y.n) : '·';
  };
  const totalBloc = somme(feuilles);

  // Blocs atelier et marque (chacun ignore son propre filtre).
  const parAt = comptes('N', 'at', (r) => r.at);
  const ateliersLignes = [...parAt.entries()].sort((a, b) => b[1].ors - a[1].ors);
  const parMq = comptes('N', 'mq', (r) => r.mq);
  const marquesLignes = [...parMq.entries()].sort((a, b) => b[1].ors - a[1].ors).slice(0, 15);
  const parMois = comptes('N', null, (r) => r.ym);
  const moisLignes = (data.mois || []).filter((m) => parMois.has(m));

  const K = [
    { l: 'OR payants', v: nb(tN.ors), d: tN1.ors ? `${tN.ors >= tN1.ors ? '+' : ''}${pct1(((tN.ors - tN1.ors) / tN1.ors) * 100)} vs N-1` : '' },
    { l: 'CA additionnel', v: eurCourt(caDe(tN)), d: caDe(tN1) ? `pièces + MO · ${caDe(tN) >= caDe(tN1) ? '+' : ''}${pct1(((caDe(tN) - caDe(tN1)) / caDe(tN1)) * 100)} vs N-1` : 'pièces + MO' },
    { l: 'Marge sur pièces', v: pct1(pDe(tN) ? (mgDe(tN) / pDe(tN)) * 100 : null), d: eurCourt(mgDe(tN)) },
    { l: 'Potentiel', v: eurCourt(potentielTotal), d: 'au taux de la meilleure concession' },
  ];

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>APV — Ventes additionnelles</h1></div>
          <div className="vt-asof"><b>{data.periode}</b>{atelier ? <span> · {atelier.toLowerCase()}</span> : null}</div>
        </div>

        {marque && (
          <div className="vj-sel">
            <span>Marque <b>{marque}</b> seulement — tous les blocs la suivent, sauf le bloc par marque.</span>
            <button type="button" className="vj-sel-x" onClick={() => f.setFiltre({ marque: '' })}>Toutes les marques</button>
          </div>
        )}

        <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
          {K.map((k) => <div className="vt-kpi" key={k.l}><div className="l">{k.l}</div><div className="v">{k.v}</div><div className="d"><small>{k.d}</small></div></div>)}
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Par concession</h2>
            <div className="st-tools">
              <span className="hint">taux de vente = part des OR payants de l'atelier contenant la famille (pièce ou MO)</span>
              <span className="st-seg">{MESURES.map((m) => <button key={m.c} type="button" className={mesure === m.c ? 'on' : ''} onClick={() => setMesure(m.c)}>{m.l}</button>)}</span>
              <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl tbl-dense">
              <thead><tr><th className="l">Concession</th><th>OR payants</th>{fams.map((fa) => <th key={fa.code}>{fa.libelle}</th>)}</tr></thead>
              <tbody>
                {arbre.map((s) => {
                  const cible = s.feuille ? s.code : (s.membres || []).join(',');
                  return (
                    <tr key={s.cle} className={`tbl-clic rg-n${s.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
                      onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
                      <td className="l">
                        <span className="rg-in" style={{ width: s.niveau * 14 }} />
                        {s.feuille ? <span className="rg-chev vide" /> : (
                          <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`} aria-label="Déplier"
                            title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par société'}
                            onClick={(e) => { e.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
                        )}
                        {s.libelle || s.nom}
                        {!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
                      </td>
                      <td>{nb(s.ors)}</td>
                      {fams.map((fa) => <td key={fa.code} className={mesure === 'taux' ? 'vt-taux' : ''}>{cellule(s, fa.code)}</td>)}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr><td className="l">Total</td><td>{nb(totalBloc.ors)}</td>{fams.map((fa) => <td key={fa.code}>{cellule(totalBloc, fa.code)}</td>)}</tr></tfoot>
            </table>
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>Par famille</h2><span className="hint">comptoir = mêmes produits vendus sans OR atelier · potentiel au taux de la meilleure concession (≥ {SEUIL_REFERENCE} OR payants)</span></div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl">
              <thead><tr><th className="l">Famille</th><th>OR avec vente</th><th>Taux de vente</th><th>vs N-1</th><th>CA pièces</th><th>CA MO</th><th>Marge pièces</th><th>Panier par OR</th><th>Comptoir</th><th>Potentiel</th></tr></thead>
              <tbody>
                {fams.map((fa) => {
                  const y = tN.f[fa.code] || vide(); const y1 = tN1.f[fa.code] || vide();
                  const t = taux(y.n, tN.ors); const t1 = taux(y1.n, tN1.ors);
                  return (
                    <tr key={fa.code}>
                      <td className="l">{fa.libelle}</td>
                      <td>{y.n ? nb(y.n) : '·'}</td>
                      <td className="vt-taux">{pct1(t)}</td>
                      <td className={t != null && t1 != null ? (t - t1 >= 0 ? 'tbl-pos' : 'tbl-neg') : ''}>{t != null && t1 != null ? pt(t - t1) : '—'}</td>
                      <td>{y.p ? eurCourt(y.p) : '·'}</td>
                      <td>{y.mo ? eurCourt(y.mo) : '·'}</td>
                      <td>{y.p ? pct1((y.mg / y.p) * 100) : '—'}</td>
                      <td>{y.n ? eurCourt((y.p + y.mo) / y.n) : '—'}</td>
                      <td>{comptoir[fa.code]?.p ? eurCourt(comptoir[fa.code].p) : '·'}</td>
                      <td>{potentiel[fa.code] ? eurCourt(potentiel[fa.code]) : '·'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr><td className="l">Total</td><td /><td /><td /><td>{eurCourt(pDe(tN))}</td><td>{eurCourt(caDe(tN) - pDe(tN))}</td>
                <td>{pct1(pDe(tN) ? (mgDe(tN) / pDe(tN)) * 100 : null)}</td><td />
                <td>{eurCourt(Object.values(comptoir).reduce((a, c) => a + c.p, 0))}</td><td>{eurCourt(potentielTotal)}</td></tr></tfoot>
            </table>
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>Par atelier</h2><span className="hint">taux de vente</span></div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl tbl-dense">
              <thead><tr><th className="l">Atelier</th><th>OR payants</th>{fams.map((fa) => <th key={fa.code}>{fa.libelle}</th>)}</tr></thead>
              <tbody>
                {ateliersLignes.map(([a, x]) => (
                  <tr key={a} className={`${ATELIERS.includes(a) ? 'tbl-clic' : ''}${atelier === a ? ' actif' : ''}`}
                    onClick={ATELIERS.includes(a) ? () => f.setFiltre({ atelier: basculer(atelier, a) }) : undefined}>
                    <td className="l">{a}</td><td>{nb(x.ors)}</td>
                    {fams.map((fa) => <td key={fa.code} className="vt-taux">{pct1(taux((x.f[fa.code] || vide()).n, x.ors))}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>Par marque</h2><span className="hint">les 15 marques aux plus nombreux OR payants · taux de vente</span></div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl tbl-dense">
              <thead><tr><th className="l">Marque</th><th>OR payants</th>{fams.map((fa) => <th key={fa.code}>{fa.libelle}</th>)}</tr></thead>
              <tbody>
                {marquesLignes.map(([m, x]) => (
                  <tr key={m} className={`tbl-clic${marque === m ? ' actif' : ''}`} onClick={() => f.setFiltre({ marque: basculer(marque, m) })}>
                    <td className="l">{m}</td><td>{nb(x.ors)}</td>
                    {fams.map((fa) => <td key={fa.code} className="vt-taux">{pct1(taux((x.f[fa.code] || vide()).n, x.ors))}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>Par mois</h2><span className="hint">taux de vente</span></div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl tbl-dense">
              <thead><tr><th className="l">Famille</th>{moisLignes.map((m) => <th key={m}>{moisCourt(m)}</th>)}</tr></thead>
              <tbody>
                <tr className="sous"><td className="l">OR payants</td>{moisLignes.map((m) => <td key={m}>{nb(parMois.get(m).ors)}</td>)}</tr>
                {fams.map((fa) => (
                  <tr key={fa.code}><td className="l">{fa.libelle}</td>
                    {moisLignes.map((m) => { const x = parMois.get(m); return <td key={m} className="vt-taux">{pct1(taux((x.f[fa.code] || vide()).n, x.ors))}</td>; })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      {Panneau}
    </>
  );
}
